import type { FormEvent } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { useCurrentUser } from '@klp/auth';
import type { IClient } from '@klp/client';
import { ClientRoutes } from '@klp/client/config';
import { Conditional } from '@klp/conditional';
import { AppRoutes } from '@klp/core/app/config';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block } from '@klp/design';
import { ErrorNotification } from '@klp/errorNotification';
import { Form, FormButton } from '@klp/form';
import { Main } from '@klp/layout';

import { KlpSupportFormView } from '../components/KlpSupportFormView';
import { SupportFormRoutes } from '../config';
import type { ISupportApplicationRequestParams } from '../interfaces';
import { useGetSupportFormQuery } from '../rtkApi';

interface IProps {
    formId?: string;
}

export const ApplicationFormView: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { formId: routeFormId } =
        useParams<ISupportApplicationRequestParams>();
    const { selectedClient } = useAppParams();

    const { currentUser: user } = useCurrentUser();

    const formId = routeFormId || props.formId;

    // Compute the query ID
    const queryId = useMemo(() => {
        if (user && selectedClient && !formId) {
            return `${selectedClient.kundeNummer}-${user.email}`;
        }
        return formId;
    }, [user, selectedClient, formId]);

    const { data: formData, isLoading: klpSupportFormLoading } =
        useGetSupportFormQuery(queryId!, { skip: !queryId });

    const klpSupportForm = formData?.klpSupportForm;

    const renderIntro = () => <Block>{t('supportForm.description')}</Block>;

    const onSubmit =
        (kundeNummer?: IClient['kundeNummer']) =>
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (kundeNummer && user) {
                history.push(
                    SupportFormRoutes.formAdd(`${kundeNummer}-${user?.email}`),
                );
            } else if (formId) {
                history.push(SupportFormRoutes.formAdd(formId));
            }
        };

    const handleClickApply =
        (kundeNummer?: IClient['kundeNummer']) =>
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();

            if (kundeNummer) {
                history.push(SupportFormRoutes.formAdd(kundeNummer));
            } else if (formId) {
                history.push(SupportFormRoutes.formAdd(formId));
            } else {
                history.push(SupportFormRoutes.form());
            }
        };

    const renderApplyButton = (
        handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void,
    ) => (
        <Block marginTop="bulky">
            <Form values={{}}>
                <FormButton
                    name="Form"
                    actionType="button"
                    disableOnError={true}
                    onClick={handleClick}
                    translation="supportForm.registerForm"
                />
            </Form>
        </Block>
    );

    return (
        <Main>
            <Conditional condition={!klpSupportFormLoading && !!user}>
                <Conditional
                    condition={!!selectedClient}
                    errorComponent={
                        <ErrorNotification
                            title="clientSelect.errorNoClientsSelected.title"
                            description="clientSelect.errorNoClientsSelected.description"
                            backTarget={AppRoutes.mainPage()}
                            backTitle="general.goBackToFrontpage"
                            mainTarget={ClientRoutes.clientSelect()}
                            mainTitle="clientSelect.chooseClient"
                            returnUrl={true}
                        />
                    }
                >
                    {renderIntro()}

                    <Conditional condition={!!klpSupportForm?.id}>
                        <KlpSupportFormView
                            klpSupportForm={klpSupportForm}
                            onSubmit={onSubmit(
                                selectedClient
                                    ? selectedClient.kundeNummer
                                    : undefined,
                            )}
                        />
                    </Conditional>

                    <Conditional
                        condition={
                            (klpSupportForm && !klpSupportForm.id) ||
                            !klpSupportForm
                        }
                    >
                        {renderApplyButton(
                            handleClickApply(
                                selectedClient
                                    ? selectedClient.kundeNummer
                                    : undefined,
                            ),
                        )}
                    </Conditional>
                </Conditional>
            </Conditional>

            <Conditional condition={!klpSupportFormLoading && !user}>
                {renderIntro()}

                <Conditional
                    condition={
                        (klpSupportForm && !klpSupportForm.id) ||
                        !klpSupportForm
                    }
                >
                    {renderApplyButton(handleClickApply())}
                </Conditional>

                <Conditional condition={!!(klpSupportForm?.id && formId)}>
                    <KlpSupportFormView
                        klpSupportForm={klpSupportForm}
                        onSubmit={onSubmit(formId)}
                    />
                </Conditional>
            </Conditional>
        </Main>
    );
};
